"use client";

import { useSDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import {
  getContextToken,
  getProductTileImpressionContext,
  getProductTileContainer,
  getProductTileClickImpressionContainerName,
  getProductTileScreenContext,
  getProductTileScreenContainerContext,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIProductTileImpressionProvider";
import {
  useGenerateBrandUrlForSDUIProductTileHandler,
  useGenerateProductLinkPropsForSDUIProductTileHandler,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/SDUIProductTileUrlHandler";
import { SDUITileInstanceRendererProps } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { ProductTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/index";
import { ProductTileType } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS } from "@faire/retailer-shared/categories/verticalProductTileCategoryPathIds";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useMemo } from "react";

export const SDUIProductTileWithBrand = ({
  tileElement,
  loggingData,
}: SDUITileInstanceRendererProps) => {
  const { on_click } = tileElement;
  const { product, brand } = tileElement.product_with_brand_info_tile ?? {};
  const brandTile = useMemo(
    () =>
      ILeanProductTile.IBrand.build({
        ...brand,
      }),
    [brand]
  );
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const generateProductLinkProps =
    useGenerateProductLinkPropsForSDUIProductTileHandler(
      product?.product?.token,
      product?.selected_product_option_token,
      product?.product?.impression_id,
      loggingData.view_display_page
    );
  const generateBrandUrl = useGenerateBrandUrlForSDUIProductTileHandler(
    product?.product?.token,
    brand?.token,
    product?.product?.impression_id,
    loggingData.view_display_page,
    isLoggedInRetailer
  );

  const brandClickLoggingData = useMemo(() => {
    return {
      ...loggingData,
      action_instance_identifier: brandTile?.token,
    };
  }, [loggingData, brandTile]);

  const onBrandClickHandler = useSDUIOnClickHandler(
    on_click,
    brandClickLoggingData,
    { logSDUIClickEvent: true, logLegacyClickEvent: true }
  );
  const onProductClickHandler = useSDUIOnClickHandler(on_click, loggingData, {
    logSDUIClickEvent: true,
    logLegacyClickEvent: true,
  });

  /**
   * This is a temporary solution to show vertical imagery for product tiles
   * in a way that is compatible with RSCs.
   * This should be removed once the BE-driven vertical tile is implemented.
   */
  const showVerticalImagery =
    !!loggingData.view_instance_identifier &&
    VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS.includes(
      loggingData.view_instance_identifier
    );
  const productTileContainer = getProductTileContainer(loggingData);
  const context = getProductTileImpressionContext(
    productTileContainer,
    loggingData
  );
  const containerName = getProductTileClickImpressionContainerName(loggingData);
  const requestId = loggingData.request_id;

  const screenContext = getProductTileScreenContext(loggingData);
  const containerContext = getProductTileScreenContainerContext(loggingData);
  const contextToken = getContextToken(loggingData);

  if (product === undefined || brand === undefined) {
    return null;
  }

  return (
    <ProductTile
      product={product}
      brand={brandTile}
      type={ProductTileType.TILE_WITH_BRAND}
      isLoggedIn={isLoggedInRetailer}
      tileInteractions={{
        tileActions: {
          onProductClick: () => onProductClickHandler(),
          onBrandClick: () => onBrandClickHandler(),
        },
        props: {
          productLinkProps: generateProductLinkProps(),
          brandLinkProps: {
            brandUrl: generateBrandUrl() ?? "",
            brandToken: brand.token ?? "",
          },
        },
      }}
      imageConfigProps={{
        showVerticalImagery,
      }}
      loggingContext={{
        impressionProviderProps: {
          context,
          containerName,
          requestId,
          screenContext,
          containerContext,
          contextToken,
          trackedLinkContainerName: containerName,
        },
      }}
    />
  );
};
